import React, { useMemo } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Avatar,
  Chip,
  Badge as MUIBadge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Dashboard,
  DirectionsCar,
  EventNote,
  Settings,
  Visibility,
  Logout,
  ChevronLeft,
  PointOfSale,
  Assessment,
  Badge,
  Payments,
  ReceiptLong,
  People,
  CalendarMonth,
  RequestQuote,
  Radar,
  // Assignment,
  // AdminPanelSettings,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useProfilePictureStore } from '../store/profilePictureStore';
import { getImageUrl } from '../utils/helpers';
import { usePermissions, type PermissionPath } from '../hooks/usePermissions';
import type { EmployeePermissions } from '../types/employee';
import CarYaLogo from './CarYaLogo';

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permission?: PermissionPath;
  anyPermission?: PermissionPath[];
  category?: keyof EmployeePermissions;
  alwaysShow?: boolean; // Show for all authenticated users
}

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuthStore();
  const { profilePictureUrl, profilePictureVersion } = useProfilePictureStore();
  const { t } = useTranslation();
  const { hasPermission, hasCategoryAccess, isOwner, currentUser } = usePermissions();

  const drawerWidth = 240;
  const collapsedWidth = 56;
  const [missingDialogOpen, setMissingDialogOpen] = React.useState(false);

  const resolvedProfilePictureUrl = profilePictureUrl
    ? getImageUrl(`${profilePictureUrl}${profilePictureUrl.includes('?') ? '&' : '?'}v=${profilePictureVersion}`)
    : undefined;

  // Filter menu items based on permissions
  const menuItems = useMemo(() => {
    const allMenuItems: MenuItem[] = [
      { label: t('dashboard.title'), path: '/dashboard', icon: <Dashboard />, permission: 'dashboard.view' },
      { label: t('pos.title'), path: '/pos', icon: <PointOfSale />, permission: 'pos.access' },
      { label: t('quotations.title'), path: '/quotations', icon: <RequestQuote />, permission: 'quotations.view' },
      { label: t('bookings.title'), path: '/bookings', icon: <EventNote />, permission: 'bookings.view' },
      { label: t('calendar.title', { defaultValue: 'Calendar' }), path: '/calendar', icon: <CalendarMonth />, permission: 'calendar.view' },
      { label: t('dashboard.clients'), path: '/clients', icon: <People />, permission: 'customers.view' },
      { label: t('vehicles.title'), path: '/vehicles', icon: <DirectionsCar />, permission: 'vehicles.view' },
      { label: t('radar.title', { defaultValue: 'Radar Tickets' }), path: '/radar', icon: <Radar />, permission: 'radar.view' },
      { label: t('analytics.title'), path: '/analytics', icon: <Assessment />, permission: 'analytics.view' },
      { label: t('revenue.title'), path: '/revenue', icon: <ReceiptLong />, permission: 'revenue.view' },
      // { label: t('revenue.sections.bank'), path: '/bank', icon: <ReceiptLong />, permission: 'finances.view' },
      { label: t('expenses.title', { defaultValue: 'Expenses' }), path: '/expenses', icon: <Payments />, permission: 'expenses.view' },
      { label: t('employees.title'), path: '/employees', icon: <Badge />, permission: 'employees.view' },
      { label: t('profile.settings', { defaultValue: 'Settings' }), path: '/profile', icon: <Settings />, alwaysShow: true },
    ];

    if (currentUser.userType === 'employee') {
      const employeePages = ['/bookings', '/calendar', '/clients', '/vehicles'];
      return allMenuItems.filter(item => employeePages.includes(item.path)
        && (item.path !== '/bookings' || hasCategoryAccess('bookings')));
    }

    return allMenuItems.filter(item => {
      // Always show items marked as alwaysShow
      if (item.alwaysShow) return true;

      // Owners see everything
      if (isOwner) return true;

      // Check specific permission
      if (item.permission && hasPermission(item.permission)) return true;
      if (item.anyPermission?.some(permission => hasPermission(permission))) return true;

      // Check category access
      if (item.category && hasCategoryAccess(item.category)) return true;

      return false;
    });
  }, [t, isOwner, hasPermission, hasCategoryAccess, currentUser.userType]);

  const missingItems = useMemo(() => {
    const items: { id: string; label: string; section: string }[] = [];
    const req = user?.requirements;

    // Only check missing items for agencies, not employees
    if (currentUser.userType === 'agency' && user) {
      if (req && !req.hasBasicInfo) items.push({ id: 'basicInfo', label: t('onboarding.reviewStep.businessInfo'), section: 'business' });
      if (req && !req.hasOwnerInfo) items.push({ id: 'ownerInfo', label: t('onboarding.reviewStep.ownerDetails'), section: 'business' });
      if (req && !req.hasAddress) items.push({ id: 'address', label: t('onboarding.reviewStep.address'), section: 'address' });
      if (req && !req.hasLocation) items.push({ id: 'location', label: t('onboarding.reviewStep.locationCoordinates'), section: 'address' });
      if (req && !req.hasDescription) items.push({ id: 'description', label: t('profile.businessDesc', { defaultValue: 'Business Description' }), section: 'business' });
      if (!user?.matriculeFiscale) items.push({ id: 'matriculeFiscale', label: t('profile.matriculeFiscale'), section: 'business' });
    }

    return items;
  }, [user, currentUser.userType, t]);

  const missingCount = missingItems.length;

  // Get display name based on user type
  const displayName = useMemo(() => {
    if (currentUser.userType === 'employee') {
      const employeeData = localStorage.getItem('employee_user');
      if (employeeData) {
        try {
          const parsed = JSON.parse(employeeData);
          return parsed.fullName || `${parsed.firstName} ${parsed.lastName}`;
        } catch {
          return 'Employee';
        }
      }
    }
    return user?.displayName || user?.businessName || 'Agency';
  }, [currentUser.userType, user]);

  // Get role chip for employees
  const roleChip = useMemo(() => {
    if (currentUser.userType === 'employee' && currentUser.role) {
      const roleColors: Record<string, string> = {
        manager: '#273B7D', // Deep Blue
        staff: '#499FDD', // Sky Blue
        driver: '#6B7280', // Gray
        accountant: '#5EBD81', // Teal Green
        receptionist: '#FFD32F', // Gold/Yellow
      };
      return {
        label: currentUser.role.replace('_', ' '),
        color: roleColors[currentUser.role] || '#9CA3AF'
      };
    }
    return null;
  }, [currentUser]);

  const handleNavigation = (path: string) => {
    if (path === '/profile' && missingCount > 0) {
      setMissingDialogOpen(true);
      return;
    }
    navigate(path);
    if (isMobile) {
      onToggle();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const handleMissingItemClick = (section: string) => {
    setMissingDialogOpen(false);
    navigate(`/profile?section=${section}`);
    if (isMobile) {
      onToggle();
    }
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
        color: '#111827',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: open ? 1.5 : 1,
          pt: open ? 1.5 : 1,
          pb: open ? 1.2 : 0.9,
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          minHeight: 56,
          overflow: 'hidden',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ overflow: 'hidden', display: 'flex', justifyContent: 'center', width: open ? 'auto' : '100%' }}>
          <CarYaLogo size={open ? 'medium' : 'small'} color="#111827" collapsed={!open} />
        </Box>
        {!isMobile && open && (
          <IconButton onClick={onToggle} size="small" sx={{ color: '#6B7280' }}>
            <ChevronLeft />
          </IconButton>
        )}
      </Box>
      <Box sx={{ height: open ? 8 : 4, flexShrink: 0 }} />


      {/* User Profile Section */}
      {open && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar
              src={resolvedProfilePictureUrl || undefined}
              sx={{
                bgcolor: '#F3F4F6',
                color: '#111827',
                width: 36,
                height: 36,
              }}
            >
              {!profilePictureUrl && (displayName?.[0] || 'A')}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                sx={{ fontSize: '0.875rem', color: '#111827' }}
              >
                {displayName}
              </Typography>
              {/* Show role chip for employees */}
              {roleChip ? (
                <Chip
                  label={roleChip.label}
                  size="small"
                  sx={{
                    bgcolor: roleChip.color,
                    color: 'white',
                    height: 20,
                    fontSize: '0.68rem',
                    mt: 0.35,
                    textTransform: 'capitalize',
                  }}
                />
              ) : (
                <Chip
                  label={t('profile.agencyOwner', { defaultValue: 'Agency Owner' })}
                  size="small"
                  sx={{
                    background: 'linear-gradient(45deg, #273B7D, #499FDD)',
                    color: 'white',
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    mt: 0.35,
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(39, 59, 125, 0.2)',
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}

      {!open && (
        <Box sx={{ px: 1, pb: 0.5, display: 'flex', justifyContent: 'center' }}>
          <Avatar
            src={resolvedProfilePictureUrl || undefined}
            sx={{
              bgcolor: '#F3F4F6',
              color: '#111827',
              width: 34,
              height: 34,
            }}
          >
            {!profilePictureUrl && (displayName?.[0] || 'A')}
          </Avatar>
        </Box>
      )}



      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 1, pb: 1 }}>
        <List sx={{ px: 0, py: 0.25 }}>
          {menuItems.map((item) => (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.15 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={isActive(item.path)}
                sx={{
                  borderRadius: 2,
                  color: '#4B5563',
                  minHeight: 36,
                  py: 0.8,
                  ...(item.path === '/profile' && missingCount > 0 && {
                    animation: 'profilePulse 1.6s ease-in-out infinite',
                    '@keyframes profilePulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(39, 59, 125, 0.35)' },
                      '70%': { boxShadow: '0 0 0 8px rgba(39, 59, 125, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(39, 59, 125, 0)' }
                    }
                  }),
                  '&.Mui-selected': {
                    bgcolor: '#F3F4F6',
                    '&:hover': {
                      bgcolor: '#F3F4F6',
                    },
                  },
                  '&:hover': {
                    bgcolor: '#F9FAFB',
                  },
                  justifyContent: open ? 'initial' : 'center',
                  px: open ? 1.5 : 1,
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive(item.path) ? '#111827' : '#6B7280',
                    minWidth: open ? 36 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {item.path === '/profile' && missingCount > 0 ? (
                    <MUIBadge
                      color="warning"
                      badgeContent={missingCount}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontWeight: 700,
                          fontSize: '0.6rem',
                          minWidth: 16,
                          height: 16,
                        }
                      }}
                    >
                      {item.icon}
                    </MUIBadge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                {open && <ListItemText primary={item.label} primaryTypographyProps={{ color: isActive(item.path) ? '#111827' : '#4B5563', fontWeight: isActive(item.path) ? 600 : 500, fontSize: '0.88rem' }} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>



      {/* Bottom Actions */}
      <Box sx={{ px: 1, pb: 1, pt: 0.5 }}>
        <List sx={{ px: 0, py: 0 }}>
          {user?.slug && (
            <ListItem disablePadding sx={{ mb: 0.15 }}>
              <ListItemButton
                onClick={() => window.open(`/agency/${user.slug}`, '_blank')}
                sx={{
                  borderRadius: 2,
                  color: '#4B5563',
                  minHeight: 34,
                  py: 0.7,
                  '&:hover': {
                    bgcolor: '#F9FAFB',
                  },
                  justifyContent: open ? 'initial' : 'center',
                  px: open ? 1.5 : 1,
                }}
              >
                <ListItemIcon
                  sx={{
                    color: '#6B7280',
                    minWidth: open ? 36 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <Visibility fontSize="small" />
                </ListItemIcon>
                {open && <ListItemText primary={t('profile.publicProfile')} primaryTypographyProps={{ color: '#4B5563', fontWeight: 500, fontSize: '0.88rem' }} />}
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                color: '#EF4444',
                minHeight: 34,
                py: 0.7,
                '&:hover': {
                  bgcolor: '#FEF2F2',
                },
                justifyContent: open ? 'initial' : 'center',
                px: open ? 1.5 : 1,
              }}
            >
              <ListItemIcon
                sx={{
                  color: '#EF4444',
                  minWidth: open ? 36 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <Logout fontSize="small" />
              </ListItemIcon>
              {open && <ListItemText primary={t('common.logout')} primaryTypographyProps={{ color: '#EF4444', fontWeight: 600, fontSize: '0.88rem' }} />}
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: open ? drawerWidth : collapsedWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: open ? drawerWidth : collapsedWidth,
              boxSizing: 'border-box',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
              borderRight: 'none',
              boxShadow: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Dialog open={missingDialogOpen} onClose={() => setMissingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('profile.completionModal.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {missingItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('profile.completionModal.allCompleted')}</Typography>
            ) : (
              <List>
                {missingItems.map((item) => (
                  <ListItem key={item.id} sx={{ px: 0 }}>
                    <ListItemText
                      primary={item.label}
                      secondary={t('profile.completionModal.required')}
                    />
                    <Button variant="outlined" size="small" onClick={() => handleMissingItemClick(item.section)}>
                      Open
                    </Button>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissingDialogOpen(false)}>Close</Button>
          {missingItems.length > 0 && (
            <Button variant="contained" onClick={() => handleMissingItemClick(missingItems[0].section)}>
              {t('common.goToSettings')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Sidebar;
